import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useContent, renderInline } from "../../lib/content";
import { Pencil, Save, RotateCcw, Search, Bold, Italic, Link as LinkIcon, ChevronDown, ChevronUp, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminContent() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const { refresh } = useContent();

  const load = () => {
    setLoading(true);
    return api
      .get("/admin/content")
      .then((r) => setBlocks(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return blocks;
    return blocks.filter((b) =>
      (b.key || "").toLowerCase().includes(q) ||
      (b.label || "").toLowerCase().includes(q) ||
      (b.page || "").toLowerCase().includes(q) ||
      (b.section || "").toLowerCase().includes(q) ||
      (b.value || "").toLowerCase().includes(q)
    );
  }, [blocks, search]);

  // Group by page
  const grouped = useMemo(() => {
    const out = {};
    for (const b of filtered) {
      const k = b.page || "Other";
      if (!out[k]) out[k] = [];
      out[k].push(b);
    }
    return out;
  }, [filtered]);

  const onSaved = () => {
    setEditingKey(null);
    load();
    refresh(); // refresh public content cache so site updates immediately
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-content-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Content</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Edit website wording</h1>
          <p className="text-sm text-[#447694] mt-1 max-w-2xl">Tweak headings, paragraphs and hyperlinks across the marketing pages. Use <code className="bg-[#9ac7f7]/20 px-1 rounded font-code text-[11px]">**bold**</code>, <code className="bg-[#9ac7f7]/20 px-1 rounded font-code text-[11px]">*italic*</code>, and <code className="bg-[#9ac7f7]/20 px-1 rounded font-code text-[11px]">[link text](https://url)</code> or <code className="bg-[#9ac7f7]/20 px-1 rounded font-code text-[11px]">[link text](/insights/your-slug)</code>.</p>
        </div>
      </div>

      <div className="mt-6 max-w-md">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blocks by page, label or content"
            className="bnp-input pl-10"
            data-testid="admin-content-search-input"
          />
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {loading && <div className="bnp-card p-10 text-center text-[#447694] text-sm">Loading</div>}

        {!loading && Object.entries(grouped).map(([page, items]) => (
          <PageGroup
            key={page}
            page={page}
            items={items}
            editingKey={editingKey}
            setEditingKey={setEditingKey}
            onSaved={onSaved}
          />
        ))}

        {!loading && filtered.length === 0 && (
          <div className="bnp-card p-10 text-center text-[#447694] text-sm">No blocks match your search.</div>
        )}
      </div>
    </div>
  );
}

function PageGroup({ page, items, editingKey, setEditingKey, onSaved }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bnp-card overflow-hidden" data-testid={`admin-content-group-${page.toLowerCase().replace(/\s+/g, "-")}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-[#9ac7f7]/10">
        <div className="flex items-center gap-3">
          <span className="bnp-chip bnp-chip-navy">{page}</span>
          <span className="font-display font-semibold text-[#04273d]">{items.length} block{items.length === 1 ? "" : "s"}</span>
        </div>
        {open ? <ChevronUp size={16} className="text-[#447694]" /> : <ChevronDown size={16} className="text-[#447694]" />}
      </button>
      {open && (
        <ul className="divide-y divide-[#9ac7f7]/20 border-t border-[#9ac7f7]/20">
          {items.map((b) => (
            <BlockRow
              key={b.key}
              block={b}
              isEditing={editingKey === b.key}
              onEdit={() => setEditingKey(b.key)}
              onCancel={() => setEditingKey(null)}
              onSaved={onSaved}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function BlockRow({ block, isEditing, onEdit, onCancel, onSaved }) {
  return (
    <li className="px-6 py-4" data-testid={`admin-content-block-${block.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-semibold text-[#04273d] text-sm">{block.section}, {block.label}</p>
            {block.is_customised && (
              <span className="bnp-chip bnp-chip-teal text-[10px] py-0.5 px-1.5">
                <CheckCircle2 size={10} /> Edited
              </span>
            )}
          </div>
          <p className="font-code text-[10px] text-[#447694] mt-0.5 truncate">{block.key}</p>
        </div>
        {!isEditing && (
          <button onClick={onEdit} className="btn-secondary text-xs py-1.5 px-3" data-testid={`admin-content-edit-${block.key}`}>
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>

      {!isEditing && (
        <div className="mt-3 bg-[#f7f7f7]/70 border border-[#9ac7f7]/20 rounded-xl px-4 py-3 text-sm text-[#04273d] leading-relaxed">
          {renderInline(block.value || block.default_value) || <span className="italic text-[#447694]">(empty)</span>}
        </div>
      )}

      {isEditing && (
        <BlockEditor block={block} onCancel={onCancel} onSaved={onSaved} />
      )}
    </li>
  );
}

function BlockEditor({ block, onCancel, onSaved }) {
  const [value, setValue] = useState(block.value || "");
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef(null);

  // Wrap selection with prefix/suffix (e.g. **) or insert template at cursor
  const wrapSelection = (before, after = before, placeholder = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? start;
    const sel = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + sel + after + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + before.length + sel.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const insertLink = () => {
    const url = window.prompt("URL (https://… or /insights/your-slug)", "https://");
    if (!url) return;
    wrapSelection("[", `](${url})`, "link text");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/content/${encodeURIComponent(block.key)}`, { value });
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this block to the original default text?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/content/${encodeURIComponent(block.key)}/reset`);
      toast.success("Reset to default.");
      onSaved();
    } catch {
      toast.error("Could not reset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-3" data-testid={`admin-content-editor-${block.key}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 flex-wrap">
        <button type="button" onClick={() => wrapSelection("**", "**", "bold text")} className="btn-ghost text-xs px-2.5" data-testid={`editor-${block.key}-bold`}>
          <Bold size={12} /> Bold
        </button>
        <button type="button" onClick={() => wrapSelection("*", "*", "italic text")} className="btn-ghost text-xs px-2.5" data-testid={`editor-${block.key}-italic`}>
          <Italic size={12} /> Italic
        </button>
        <button type="button" onClick={insertLink} className="btn-ghost text-xs px-2.5" data-testid={`editor-${block.key}-link`}>
          <LinkIcon size={12} /> Add link
        </button>
        <span className="flex-1" />
        <button type="button" onClick={reset} disabled={busy} className="btn-ghost text-xs px-2.5 text-amber-700" data-testid={`editor-${block.key}-reset`}>
          <RotateCcw size={12} /> Reset to default
        </button>
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={Math.max(3, Math.min(10, value.split("\n").length + 2))}
        className="bnp-input font-code text-sm"
        data-testid={`editor-${block.key}-textarea`}
      />

      {/* Live preview */}
      <div className="bg-[#f7f7f7]/70 border border-[#9ac7f7]/20 rounded-xl px-4 py-3">
        <p className="text-[10px] text-[#447694] tracking-widest uppercase font-semibold mb-1.5">Live preview</p>
        <div className="text-sm text-[#04273d] leading-relaxed">
          {renderInline(value) || <span className="italic text-[#447694]">(empty)</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid={`editor-${block.key}-save`}>
          <Save size={12} /> {busy ? "Saving" : "Save"}
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm" data-testid={`editor-${block.key}-cancel`}>
          <X size={12} /> Cancel
        </button>
        <span className="flex-1" />
        <p className="text-[11px] text-[#447694]">
          Default: <span className="italic">{block.default_value.length > 80 ? block.default_value.slice(0, 80) + "…" : block.default_value}</span>
        </p>
      </div>
    </div>
  );
}
